import { useState, type FormEvent } from 'react';
import { addDoc, collection } from 'firebase/firestore';
import { CheckCircle2, Clock, Mail, MapPin, Phone, Send, Star } from 'lucide-react';
import { db, firebaseReady } from '../lib/firebase';
import { useSiteData } from '../context/SiteDataContext';
import Reveal from '../components/Reveal';
import { DAY_KEYS, DAY_LABELS } from '../lib/types';

export default function Contact() {
  const { settings } = useSiteData();

  const [msg, setMsg] = useState({ name: '', email: '', phone: '', text: '' });
  const [msgStatus, setMsgStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const [review, setReview] = useState({ name: '', vehicle: '', rating: 5, text: '' });
  const [reviewStatus, setReviewStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const sendMessage = async (e: FormEvent) => {
    e.preventDefault();
    if (!firebaseReady) {
      window.location.href = `mailto:${settings.email}?subject=Website message from ${msg.name}&body=${encodeURIComponent(msg.text)}`;
      return;
    }
    setMsgStatus('sending');
    try {
      await addDoc(collection(db(), 'quotes'), {
        createdAt: Date.now(),
        status: 'new',
        serviceName: 'General contact message',
        customer: { name: msg.name, email: msg.email, phone: msg.phone },
        vehicle: '',
        details: msg.text,
        budget: '',
        adminNotes: '',
      });
      setMsgStatus('sent');
    } catch {
      setMsgStatus('error');
    }
  };

  const sendReview = async (e: FormEvent) => {
    e.preventDefault();
    if (!firebaseReady) {
      setReviewStatus('error');
      return;
    }
    setReviewStatus('sending');
    try {
      await addDoc(collection(db(), 'testimonials'), {
        createdAt: Date.now(),
        name: review.name,
        vehicle: review.vehicle,
        rating: review.rating,
        text: review.text,
        approved: false,
        featured: false,
      });
      setReviewStatus('sent');
    } catch {
      setReviewStatus('error');
    }
  };

  return (
    <div>
      <section className="relative border-b border-white/[0.06]">
        <div className="hero-grid absolute inset-0" />
        <div className="container-x relative py-16 text-center sm:py-20">
          <Reveal>
            <span className="eyebrow">Contact</span>
            <h1 className="heading-xl mt-4">Let's Talk Cars</h1>
            <p className="mx-auto mt-5 max-w-xl text-lg text-zinc-400">
              Questions, quotes, or just want to talk through what your car needs — reach out any way you like.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="container-x grid gap-8 py-16 lg:grid-cols-[380px_1fr]">
        {/* Info column */}
        <Reveal>
          <div className="space-y-4">
            <a href={`tel:${settings.phone.replace(/[^0-9+]/g, '')}`} className="card glass-hover flex items-center gap-4 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-accent-600 to-sky-500 text-white"><Phone size={20} /></span>
              <span>
                <span className="block text-xs uppercase tracking-wider text-zinc-500">Call or Text</span>
                <span className="font-display font-semibold text-white">{settings.phone}</span>
              </span>
            </a>
            <a href={`mailto:${settings.email}`} className="card glass-hover flex items-center gap-4 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-accent-600 to-sky-500 text-white"><Mail size={20} /></span>
              <span>
                <span className="block text-xs uppercase tracking-wider text-zinc-500">Email</span>
                <span className="font-display font-semibold text-white">{settings.email}</span>
              </span>
            </a>
            <div className="card flex items-center gap-4 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-accent-600 to-sky-500 text-white"><MapPin size={20} /></span>
              <span>
                <span className="block text-xs uppercase tracking-wider text-zinc-500">Location</span>
                <span className="font-display font-semibold text-white">{settings.city}</span>
                <span className="block text-sm text-zinc-400">{settings.serviceArea}{settings.travel.mode !== 'off' ? ' · mobile service available' : ''}</span>
              </span>
            </div>
            <div className="card p-5">
              <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500">
                <Clock size={13} /> Hours
              </span>
              <ul className="mt-3 space-y-1.5 text-sm">
                {DAY_KEYS.map((d) => {
                  const h = settings.hours[d];
                  return (
                    <li key={d} className="flex justify-between text-zinc-400">
                      <span>{DAY_LABELS[d]}</span>
                      <span className={h?.closed ? 'text-zinc-600' : 'text-zinc-200'}>
                        {h?.closed ? 'Closed' : `${h?.open} – ${h?.close}`}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </Reveal>

        {/* Forms column */}
        <div className="space-y-8">
          <Reveal delay={100}>
            {msgStatus === 'sent' ? (
              <div className="card flex flex-col items-center gap-3 p-10 text-center">
                <CheckCircle2 size={40} className="text-emerald-400" />
                <h3 className="font-display text-xl font-bold text-white">Message sent!</h3>
                <p className="text-zinc-400">We'll get back to you as soon as we can.</p>
              </div>
            ) : (
              <form onSubmit={sendMessage} className="card grid gap-4 p-7 sm:grid-cols-2">
                <h2 className="font-display text-xl font-bold text-white sm:col-span-2">Send us a message</h2>
                <div>
                  <label className="label">Name</label>
                  <input className="input" value={msg.name} onChange={(e) => setMsg({ ...msg, name: e.target.value })} required />
                </div>
                <div>
                  <label className="label">Phone or Email</label>
                  <input className="input" value={msg.phone} onChange={(e) => setMsg({ ...msg, phone: e.target.value })} required />
                </div>
                <div className="sm:col-span-2">
                  <label className="label">Message</label>
                  <textarea className="input min-h-28" value={msg.text} onChange={(e) => setMsg({ ...msg, text: e.target.value })} required />
                </div>
                {msgStatus === 'error' && (
                  <p className="text-sm text-red-400 sm:col-span-2">Couldn't send — please try again or call us.</p>
                )}
                <div className="sm:col-span-2">
                  <button className="btn-primary" disabled={msgStatus === 'sending'}>
                    {msgStatus === 'sending' ? 'Sending…' : <>Send Message <Send size={15} /></>}
                  </button>
                </div>
              </form>
            )}
          </Reveal>

          <Reveal delay={200}>
            {reviewStatus === 'sent' ? (
              <div className="card flex flex-col items-center gap-3 p-10 text-center">
                <CheckCircle2 size={40} className="text-emerald-400" />
                <h3 className="font-display text-xl font-bold text-white">Thanks for the review!</h3>
                <p className="text-zinc-400">It'll appear on the site once we approve it.</p>
              </div>
            ) : (
              <form onSubmit={sendReview} className="card grid gap-4 p-7 sm:grid-cols-2">
                <h2 className="font-display text-xl font-bold text-white sm:col-span-2">Had a detail with us? Leave a review</h2>
                <div>
                  <label className="label">Name</label>
                  <input className="input" value={review.name} onChange={(e) => setReview({ ...review, name: e.target.value })} required />
                </div>
                <div>
                  <label className="label">Vehicle (optional)</label>
                  <input className="input" value={review.vehicle} onChange={(e) => setReview({ ...review, vehicle: e.target.value })} placeholder="2021 F-150" />
                </div>
                <div className="sm:col-span-2">
                  <label className="label">Rating</label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((r) => (
                      <button key={r} type="button" onClick={() => setReview({ ...review, rating: r })} aria-label={`${r} stars`}>
                        <Star size={26} className={r <= review.rating ? 'fill-amber-400 text-amber-400' : 'text-zinc-600 hover:text-zinc-400'} />
                      </button>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="label">Your Review</label>
                  <textarea className="input min-h-24" value={review.text} onChange={(e) => setReview({ ...review, text: e.target.value })} required maxLength={2000} />
                </div>
                {reviewStatus === 'error' && (
                  <p className="text-sm text-red-400 sm:col-span-2">Couldn't submit the review right now — please try again later.</p>
                )}
                <div className="sm:col-span-2">
                  <button className="btn-ghost" disabled={reviewStatus === 'sending'}>
                    {reviewStatus === 'sending' ? 'Submitting…' : 'Submit Review'}
                  </button>
                </div>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
